// Matching a scanned vehicle tag ("v|{vehicleId}|{vrm}|{vin}") against the vehicle
// on the job in view. The id is authoritative when it agrees, but a label can
// carry a different id for the same car (a second Vehicle row for that reg, or
// a keyboard-wedge scanner dropping a character from the GUID), so the reg
// printed on the tag is accepted as a match too - it's what the operator is
// actually looking at. VIN-keyed yards print the VIN instead of a plate, so the
// VIN is the third thing tried.

/** The QR encodes a missing reg or VIN as the literal "NULL". */
const NULL_VRM = "NULL";

/** Uppercase, no whitespace, so "fh62 wmv" and "FH62WMV" compare equal. */
export const normaliseVrm = (vrm?: string | null): string =>
  (vrm ?? "").replace(/\s+/g, "").toUpperCase();

const normaliseScannedVin = (vin?: string | null): string => (vin ?? "").replace(/\s+/g, "").toUpperCase();

const placeholderToUndefined = (value?: string | null): string | undefined =>
  value && value.toUpperCase() !== NULL_VRM ? value : undefined;

export interface ScannedVehicle {
  id?: string;
  vrm?: string;
  vin?: string;
}

export interface VehicleIdentity {
  id?: string | null;
  vrm?: string | null;
  combinedVIN?: string | null;
  vinVerified?: string | null;
}

/**
 * The payload printed into a vehicle label's QR code. Four segments: the third is the plate and
 * the fourth the VIN, each "NULL" when missing. Readers that only know the three-segment form
 * (older labels, older readers) still work: they read the first three and ignore the rest.
 */
export const buildVehicleScanPayload = (vehicle: VehicleIdentity): string =>
  `v|${vehicle.id ?? ""}|${vehicle.vrm || NULL_VRM}|${vehicle.vinVerified || vehicle.combinedVIN || NULL_VRM}`;

/** Reads a vehicle scan's parts; null when the scan is not a vehicle tag. */
export const parseVehicleScan = (parts: string[]): ScannedVehicle | null => {
  if (parts[0]?.toLowerCase() !== "v") return null;

  return {
    id: parts[1] || undefined,
    vrm: placeholderToUndefined(parts[2]),
    vin: placeholderToUndefined(parts[3]),
  };
};

export function scanMatchesVehicle(
  scanned: ScannedVehicle,
  vehicle: VehicleIdentity | undefined | null
): boolean {
  if (!vehicle) return false;

  const scannedId = scanned.id?.trim().toLowerCase();
  const vehicleId = vehicle.id?.trim().toLowerCase();
  if (scannedId && vehicleId && scannedId === vehicleId) return true;

  const scannedVrm = normaliseVrm(scanned.vrm);
  const vehicleVrm = normaliseVrm(vehicle.vrm);
  if (scannedVrm && scannedVrm !== NULL_VRM && vehicleVrm && scannedVrm === vehicleVrm) return true;

  const scannedVin = normaliseScannedVin(scanned.vin);
  const vehicleVin = normaliseScannedVin(vehicle.vinVerified || vehicle.combinedVIN);
  if (scannedVin && scannedVin !== NULL_VRM && vehicleVin && scannedVin === vehicleVin) return true;

  return false;
}
